import React from 'react';

import {CircularProgress, Tooltip} from '@mui/material';

import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import type {Theme} from '@netx/core-components/Theme';

import type {AttributeAttributes} from '@netx/core-asset/models/attribute';
import {useAttributesStore} from '@netx/core-attribute/store/attributesStore';

import {Icon, faBullseye, faKey} from '@netx/core-icons';

const styles = (theme: Theme) => {
	const ThemeProps = theme.components || {};
	const TextFieldProps = ThemeProps.MuiTextField || {};
	const InputVariant = TextFieldProps?.defaultProps?.variant || 'standard';

	return createStyles({
		inputAdornmentStart: {
			alignItems: 'center',
			display: 'inline-flex',
			flex: '0 0 auto',
			justifyContent: 'center',
			borderRadius: InputVariant === 'outlined' ? '4px 0 0 4px' : undefined,
			border:
				InputVariant === 'outlined'
					? `1px solid ${theme.palette.custom.border}`
					: undefined,
			borderRight: 0,
			marginRight: 0,
			padding: theme.spacing(0, 1),
			'& + div [class*="MuiOutlinedInput-root"]': {
				borderRadius: '0 4px 4px 0',
			},
		},
	});
};

export interface MetadataLookupAdornmentProps {
	attrData: AttributeAttributes;
	attributeWorking?: boolean;
	disabled?: boolean;
	isMetadataLookup?: boolean;
	isMetadataTarget?: boolean;
}

const MetadataLookupAdornment = ({
	classes,

	attrData,
	attributeWorking = false,
	disabled = false,
	isMetadataLookup = false,
	isMetadataTarget = false,
}: WithStyles<typeof styles> & MetadataLookupAdornmentProps) => {
	if (!isMetadataLookup && !isMetadataTarget) return null;

	const title = isMetadataLookup
		? 'Metadata lookup key'
		: 'Metadata lookup target';

	const icon = isMetadataLookup ? faKey : faBullseye;

	const attributeNames = new Set<string>();

	if (isMetadataLookup) {
		attrData.keyDatasets.forEach((attributeId) => {
			useAttributesStore.getState().attributes.forEach((attribute) => {
				if ((attribute.targetDatasets || []).includes(attributeId)) {
					attributeNames.add(attribute.name);
				}
			});
		});
	} else {
		attrData.targetDatasets.forEach((attributeId) => {
			useAttributesStore.getState().attributes.forEach((attribute) => {
				if ((attribute.keyDatasets || []).includes(attributeId)) {
					attributeNames.add(attribute.name);
				}
			});
		});
	}

	return (
		<React.Fragment>
			<Tooltip
				title={
					<React.Fragment>
						<div>{title}</div>
						<table>
							<tbody>
								{[...attributeNames].map((attributeName) => (
									<tr key={attributeName}>
										<td>{attributeName}</td>
									</tr>
								))}
							</tbody>
						</table>
					</React.Fragment>
				}
				placement="top"
				style={{
					opacity: disabled ? 0.7 : undefined,
					pointerEvents: disabled ? 'none' : undefined,
				}}
			>
				<div className={classes.inputAdornmentStart}>
					{(attributeWorking && !isMetadataLookup && (
						<CircularProgress size={24} />
					)) || <Icon icon={icon} />}
				</div>
			</Tooltip>
		</React.Fragment>
	);
};

export default withStyles(styles, {
	name: 'Attributes-Attribute-MetadataLookupAdornment',
})(MetadataLookupAdornment);
